import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Loader2, Mail, Lock, ShieldCheck } from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/12359215/pexels-photo-12359215.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const { support_email } = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null); // { mfa_token, email }
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.mfa_required) {
        setMfa({ mfa_token: res.mfa_token, email: res.email });
      } else {
        navigate(next);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await verifyTwoFactor(mfa.mfa_token, code);
      navigate(u.role === "admin" && next === "/dashboard" ? "/admin" : next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-[calc(100vh-73px)] lg:grid-cols-2">
      <div className="flex items-center justify-center px-5 py-12 lg:px-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#2C3830] text-white"><Building2 className="h-5 w-5" /></span>
            <span className="font-display text-2xl font-semibold text-[#2C3830]">RENTO</span>
          </div>

          {!mfa ? (
            <>
              <h1 className="mt-8 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]">Welcome back</h1>
              <p className="mt-2 text-[#6B7280]">Sign in to manage your listings.</p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                    <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required className="rounded-xl pl-9" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
                    <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="rounded-xl pl-9" />
                  </div>
                </div>
                {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
                {/\bsuspend/i.test(error) && support_email && (
                  <a
                    data-testid="appeal-link"
                    href={`mailto:${support_email}?subject=${encodeURIComponent("Appeal my RENTO account suspension")}&body=${encodeURIComponent(`Hello RENTO support,\n\nMy account (${email}) has been suspended and I would like to appeal this decision.\n\n`)}`}
                    className="block text-sm font-medium text-[#2C3830] underline"
                  >
                    Appeal this suspension / contact support
                  </a>
                )}
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#2C3830] py-6 text-white hover:bg-[#3A4A40]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
                </Button>
              </form>

              <div className="my-6 flex items-center gap-3 text-xs text-[#6B7280]">
                <span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" />
              </div>
              <Button data-testid="login-google" onClick={google} variant="outline" className="w-full rounded-full py-6">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-5 w-5" />
                Continue with Google
              </Button>

              <p className="mt-6 text-center text-sm text-[#6B7280]">
                New to RENTO? <Link to="/register" data-testid="go-register" className="font-medium text-[#2C3830] underline">Create an account</Link>
              </p>
            </>
          ) : (
            <>
              <div className="mt-8 flex items-center gap-2 text-[#2C3830]">
                <ShieldCheck className="h-5 w-5" />
                <h1 className="font-display text-2xl font-medium tracking-tight text-[#1A1A1A]">Verify it's you</h1>
              </div>
              <p className="mt-2 text-sm text-[#6B7280]">
                We sent a 6-digit code to <strong>{mfa.email}</strong>. Enter it below to finish signing in.
              </p>
              <form onSubmit={verify} className="mt-8 space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Verification code</Label>
                  <Input
                    data-testid="mfa-code"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    required
                    className="rounded-xl text-center text-2xl tracking-[0.5em]"
                  />
                </div>
                {error && <p data-testid="mfa-error" className="text-sm text-destructive">{error}</p>}
                <Button data-testid="mfa-submit" type="submit" disabled={loading || code.length !== 6} className="w-full rounded-full bg-[#2C3830] py-6 text-white hover:bg-[#3A4A40]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & sign in"}
                </Button>
                <button type="button" data-testid="mfa-back" onClick={() => { setMfa(null); setCode(""); setError(""); }} className="w-full text-center text-sm text-[#6B7280] underline">
                  Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2C3830]/40" />
      </div>
    </div>
  );
}
